import { format } from "date-fns";
import { computed, watch, type ComputedRef, type Ref } from "vue";

import type { Week } from "@ctrl/controller-rpc";

import * as inject from "@/components/inject";
import { useBrowserStore } from "@/composables/useBrowserStore";
import type { UseProfile } from "@/composables/useProfile";

/**
 * Ranking algorithm for shared task-list order.
 *
 * - `strict` — ordered id array in local storage (current). Deterministic,
 *   simple, no per-item rank keys.
 * - `fractional` — reserved for a future id → rank-key map (e.g. "a0", "a1"
 *   / 1000, 2000-style indexes) so inserts need not rewrite neighbors.
 */
export type TaskListOrderMode = "strict" | "fractional";

/** Active algorithm. Flip to `"fractional"` once that strategy is implemented. */
export const TASK_LIST_ORDER_MODE: TaskListOrderMode = "strict";

/** Per-list ranking under `strict`: visible order is exactly this id array. */
export type StrictTaskListRanking = readonly string[];

/**
 * Per-list ranking under `fractional` (not implemented yet): id → rank key.
 * Keys sort lexicographically (or numerically if encoded that way).
 */
export type FractionalTaskListRanking = Record<string, string>;

export type TaskListOrderStore = {
  mode: TaskListOrderMode;
  lists: Record<string, StrictTaskListRanking>;
};

export const TASK_LIST_ORDER_STORE_KEY = "task-list-order";

/**
 * List id segments for {@link orgSectionKey}. Every ranking key uses that
 * path so personal and team orgs cannot collide.
 */
export const taskListKeys = {
  /** Unplanned list — personal org (Tasks / Agenda / Planner) and team org. */
  unplanned: "unplanned",
} as const;

/** List id for a calendar day (compose with {@link orgSectionKey}). */
export function dayKey(date: Date): string {
  return `day:${format(date, "yyyy-MM-dd")}`;
}

/** List id for a calendar week (compose with {@link orgSectionKey}). */
export function weekKey(week: Week): string {
  return `week:${week.year}:${week.n}`;
}

/**
 * Storage key for a task-list ranking. Same layout for personal and team
 * orgs — different org/workspace ids keep rankings dissociated.
 */
export function orgSectionKey(
  orgId: string,
  workspaceId: string,
  listKey: string
): string {
  return `orderings/organizations/${orgId}/workspaces/${workspaceId}/lists/${listKey}`;
}

/** {@link orgSectionKey} for the user's personal organization / workspace. */
export function personalSectionKey(
  profile: UseProfile,
  listKey: string
): string {
  const { organizationId, workspaceId } = profile.user.personal;
  return orgSectionKey(organizationId, workspaceId, listKey);
}

type TaskListOrderStrategy = {
  apply: (
    currentIds: readonly string[],
    stored: StrictTaskListRanking
  ) => string[];
  ensureDefault: (
    store: Ref<TaskListOrderStore>,
    listKey: string,
    currentIds: readonly string[]
  ) => void;
  reorder: (
    stored: StrictTaskListRanking,
    visibleAfter: readonly string[]
  ) => string[];
};

/**
 * Merge controller ids with a strict stored ranking.
 * Unranked ids (not in storage) go to the top in controller order; ranked ids
 * keep stored relative order. Ids in storage but not in `currentIds` are kept
 * in storage only (not returned).
 */
export function applyStrictTaskListOrder(
  currentIds: readonly string[],
  stored: readonly string[]
): string[] {
  const storedSet = new Set(stored);
  const currentSet = new Set(currentIds);
  const unranked = currentIds.filter((id) => !storedSet.has(id));
  const ranked = stored.filter((id) => currentSet.has(id));
  return [...unranked, ...ranked];
}

/**
 * After a drag within a list, replace the visible ids' block in `stored` with
 * `visibleAfter` (same membership, new order). Non-visible ids stay put.
 */
export function reorderStrictTaskList(
  stored: readonly string[],
  visibleAfter: readonly string[]
): string[] {
  const visibleSet = new Set(visibleAfter);
  if (visibleSet.size === 0) return [...stored];

  const result: string[] = [];
  let inserted = false;

  for (const id of stored) {
    if (visibleSet.has(id)) {
      if (!inserted) {
        result.push(...visibleAfter);
        inserted = true;
      }
    } else {
      result.push(id);
    }
  }

  if (!inserted) {
    return [...visibleAfter, ...stored];
  }

  return result;
}

const strictStrategy: TaskListOrderStrategy = {
  apply: applyStrictTaskListOrder,
  ensureDefault: (store, listKey, currentIds) => {
    if (currentIds.length === 0) return;
    if (Object.prototype.hasOwnProperty.call(store.value.lists, listKey)) {
      return;
    }
    store.value = {
      mode: TASK_LIST_ORDER_MODE,
      lists: {
        ...store.value.lists,
        [listKey]: [...currentIds],
      },
    };
  },
  reorder: reorderStrictTaskList,
};

const fractionalStrategy: TaskListOrderStrategy = {
  apply: () => {
    throw new Error(
      'Fractional task-list order is not implemented; keep TASK_LIST_ORDER_MODE "strict".'
    );
  },
  ensureDefault: () => {
    throw new Error(
      'Fractional task-list order is not implemented; keep TASK_LIST_ORDER_MODE "strict".'
    );
  },
  reorder: () => {
    throw new Error(
      'Fractional task-list order is not implemented; keep TASK_LIST_ORDER_MODE "strict".'
    );
  },
};

export function taskListOrderStrategy(
  mode: TaskListOrderMode = TASK_LIST_ORDER_MODE
): TaskListOrderStrategy {
  switch (mode) {
    case "strict":
      return strictStrategy;
    case "fractional":
      return fractionalStrategy;
  }
}

/**
 * Merge controller ids with a stored ranking (dispatches on
 * {@link TASK_LIST_ORDER_MODE}).
 */
export function applyTaskListOrder(
  currentIds: readonly string[],
  stored: readonly string[],
  mode: TaskListOrderMode = TASK_LIST_ORDER_MODE
): string[] {
  return taskListOrderStrategy(mode).apply(currentIds, stored);
}

/**
 * Persist controller order the first time a list is seen so every view of that
 * list shares the same ranking (instead of each re-deriving from live RPCs).
 */
export function ensureDefaultTaskListOrder(
  store: Ref<TaskListOrderStore>,
  listKey: string,
  currentIds: readonly string[]
): void {
  taskListOrderStrategy(TASK_LIST_ORDER_MODE).ensureDefault(
    store,
    listKey,
    currentIds
  );
}

/**
 * After a drag within a list, replace the visible ids' block in `stored` with
 * `visibleAfter` (same membership, new order). Non-visible ids stay put.
 */
export function reorderTaskList(
  stored: readonly string[],
  visibleAfter: readonly string[],
  mode: TaskListOrderMode = TASK_LIST_ORDER_MODE
): string[] {
  return taskListOrderStrategy(mode).reorder(stored, visibleAfter);
}

let orderStore: Ref<TaskListOrderStore> | null = null;
let orderStoreAccountId: string | null = null;

/** App-lifetime store so planner sidebar and footer stay in sync. Never released. */
export function getTaskListOrderStore(
  accountId: string
): Ref<TaskListOrderStore> {
  if (orderStore && orderStoreAccountId === accountId) {
    return orderStore;
  }
  const { syncState } = useBrowserStore(accountId);
  orderStore = syncState<TaskListOrderStore>(
    TASK_LIST_ORDER_STORE_KEY,
    { mode: TASK_LIST_ORDER_MODE, lists: {} },
    { deep: true }
  );
  orderStoreAccountId = accountId;
  return orderStore;
}

export type OrderableTaskList = {
  /** Ids after applying the stored ranking (live ref for Sortable binding). */
  orderedIds: Ref<string[]>;
  /** Read-only ordered view when not mutating. */
  orderedIdsReadonly: ComputedRef<string[]>;
  commitOrder: (visibleAfter: readonly string[]) => void;
  release: () => void;
};

export type UseOrderableTaskListOptions = {
  /**
   * Pass when calling from async setup after an `await` (inject is
   * unavailable). Otherwise the hook reads `inject.profile()`.
   */
  profile?: Ref<UseProfile>;
};

export function useOrderableTaskList(
  listKey: Ref<string | undefined>,
  currentIds: Ref<string[] | null | undefined>,
  options?: UseOrderableTaskListOptions
): OrderableTaskList {
  const storeAccountId =
    options?.profile?.value.user.accountId ??
    inject.profile().value.user.accountId;
  const store = getTaskListOrderStore(storeAccountId);
  const strategy = taskListOrderStrategy(TASK_LIST_ORDER_MODE);

  const seedDefaultOrder = () => {
    const key = listKey.value;
    const ids = currentIds.value;
    if (!key || !ids) return;
    ensureDefaultTaskListOrder(store, key, ids);
  };

  seedDefaultOrder();
  const stopSeed = watch([listKey, currentIds], seedDefaultOrder);

  const orderedIdsReadonly = computed(() => {
    const key = listKey.value;
    const ids = currentIds.value;
    if (!key || !ids) return ids ?? [];
    const stored = store.value.lists[key] ?? [];
    return strategy.apply(ids, stored);
  });

  const orderedIds = computed({
    get: () => orderedIdsReadonly.value,
    set: (visibleAfter: string[]) => {
      const key = listKey.value;
      if (!key) return;
      const stored = store.value.lists[key] ?? [];
      store.value = {
        mode: TASK_LIST_ORDER_MODE,
        lists: {
          ...store.value.lists,
          [key]: strategy.reorder(stored, visibleAfter),
        },
      };
    },
  });

  const commitOrder = (visibleAfter: readonly string[]) => {
    orderedIds.value = [...visibleAfter];
  };

  return {
    orderedIds,
    orderedIdsReadonly,
    commitOrder,
    release: () => stopSeed(),
  };
}
